import styled, { css } from 'styled-components';
import { pxToRem } from 'utils/convert';
import { Divider } from 'ui/divider';
import { Trigger } from '@radix-ui/react-popover';

import {
  body1,
  body2,
  caption,
  colors,
  fontSizes,
  smallShadow,
  largeShadow,
  spacing,
  subtitle1,
  subtitle2,
  fontWeights,
} from 'ui/theme';
import { GhostButton } from 'ui/button';
import { BasicSelect } from 'ui/selects';
import { Badge } from 'ui/badge';

export const QueryBaseContainer = styled.div`
  display: flex;
  padding: ${spacing.half} ${spacing.threeQuarter};
  position: relative;
  gap: ${spacing.half};
  &:hover {
      background: ${colors.grey1};
      border-radius: ${spacing.quarter};
      button {
        opacity: 1;
    }
  }
`;

export const QueryBaseContainerAndQuery = styled(QueryBaseContainer)`
  margin-top: ${spacing.plusHalf};
  display: flex;
  align-items: center;
  :hover {
    background: transparent;
    border-radius: none;
  }
  gap: ${spacing.half};
`;
/** Relative position crucial for connecting line positions  */
export const LeftCol = styled.div`
  position: relative;
`;

export const UpdatedLeftCol = styled.div`
  min-width: ${pxToRem(180)};
`;

export const QueryBuilderLeadText = styled.div`
  padding-right: ${spacing.plusHalf};
  white-space: nowrap;
  display: flex;
  justify-content: flex-start;
  font-size: ${fontSizes.s14};
  font-weight: ${fontWeights.semiBold};
  line-height: ${fontSizes.s14};
  margin-top: ${spacing.half};
  min-height: ${spacing.plusHalf};
  align-items: center;
`;

export const QueryBuilderFollowingText = styled(QueryBuilderLeadText)`
  display: flex;
  align-items: center;
  min-height: ${spacing.plusHalf};
  margin: ${spacing.half} ${spacing.half} ${spacing.half} 0;
`;

export const QueryBuilderWrapper = styled.div`
  background: ${colors.grey1};
  padding: ${spacing.double};
  border-radius: ${spacing.half} ${spacing.half} 0 0;
`;

export const LeadTextMustHave = styled(QueryBuilderLeadText)`
  color: ${colors.textSecondary};
  min-width: 0;
  padding-right: ${spacing.base};
`;

export const StyledBasicSelect = styled(BasicSelect)`
  margin-right: ${spacing.half};
`;

export const MidCol = styled.div`
  align-items: center;
  width: 100%;
  padding-left: ${spacing.base};
`;

export const UpdatedMidCol = styled.div`
  align-items: center;
  width: 100%;
  display: flex;
`;

export const UpdatedMidColWithPurchaseEvent = styled(UpdatedMidCol)`
  align-items: flex-start;
`;

export const QueryCloseButton = styled(GhostButton)`
  opacity: 0;
  &:focus {
    opacity: 1;
  }
  &:hover {
    opacity: 1;
  }
`;

export const RightCol = styled.div`
  min-width: ${spacing.base};
  cursor: pointer;
  display: flex;
`;

export const InputGroup = styled.div`
  display: flex;
  > * {
    margin-right: ${spacing.threeQuarter};
  }
`;

export const UpdatedInputGroup = styled.div`
  display: flex;
  gap: ${spacing.half};
`;

export const StyledPrefix = styled.span`
  color: ${colors.purple90} !important;
`;

export const EventBaseControlsGroup = styled.div`
  display: flex;
  > * {
    margin-left: ${spacing.threeQuarter};
  }
`;

export const RowFlexSpaceBetween = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const EventConditionContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${spacing.half};
  ${body2}
  position: relative;
  left: -104px;
`;

export const LeftColCondition = styled.div`
  width: ${spacing.x16};
`;

export const UpdatedLeftColCondition = styled.div`
  width: ${pxToRem(400)};
  display: flex;
  gap: ${spacing.half};
  align-items: center;
  padding-left: ${spacing.double};
`;

export const EventConditionInputContainer = styled.div`
  display: flex;
  align-items: center;
  margin: 0 ${spacing.half};
`;

export const AndQueryContainer = styled.div`
  background: ${colors.white};
  padding: ${spacing.base};
  border-radius: ${spacing.half};
  ${smallShadow}
  width: 100%;
  margin-bottom: ${spacing.base};
  div:first-child > .queryConnectingLine {
    top: 48px;
    height: calc(100% - 24px);
  }
  div:nth-last-child(2) > .queryConnectingLine {
    border: none;
  }
`;

export const QueryContentsContainer = styled.div`
  padding: 0;
`;

export const QueryBuilderDataContainer = styled.div`
  padding: ${spacing.base} ${spacing.plusHalf} ${spacing.plusHalf} ${spacing.plusHalf};
`;

export const QueryBuilderTitle = styled.div`
  ${subtitle1}
`;

export const ActionButtons = styled.div`
  display: flex;
  align-items: center;
`;

export const OrActionButtons = styled(ActionButtons)`
  gap: 0;
  button:first-of-type {
    margin-right: ${spacing.threeQuarter};
  }
`;

export const AndActionButtons = styled(ActionButtons)`
  button:first-of-type {
    margin-right: ${spacing.threeQuarter};
  }
`;

export const UpdatedOrActionButtons = styled(ActionButtons)`
  gap: ${spacing.base};
  button:first-of-type {
    margin-right: 0;
  }
`;

export const UpdatedButtonWrapper = styled.div`
  gap: ${spacing.threeQuarter};
  display: flex;
`;

export const UpdatedAndActionButtons = styled(ActionButtons)`
  gap: ${spacing.base};
`;

export const QueryBuilderText = styled.span`
  ${body2}
  margin-right: ${spacing.half};
`;

export const LeadTextNewGroup = styled(QueryBuilderText)`
  color: ${colors.textSecondary};
`;

export const StyledDivider = styled(Divider)`
  margin: ${spacing.plusHalf} 0;
`;

export const DurationSelection = styled.div`
  background: ${colors.grey5};
  padding: ${spacing.plusHalf};
  margin-top: ${spacing.base};
  display: flex;
  align-items: center;
`;

const durationChinBase = css`
  background: ${colors.grey1};
  border-radius: 0 0 ${spacing.half} ${spacing.half};
  margin-top: 0;
  padding: ${spacing.threeQuarter} ${spacing.plusHalf} ${spacing.threeQuarter};
  white-space: pre;
`;

export const GoalCriteriaReadDurationChin = styled(DurationSelection)`
  ${durationChinBase}
  ${smallShadow}
`;

export const GoalCriteriaText = styled.p`
  ${body1}
  margin: 0;
`;

export const ChinSpacing = styled.div`
  margin-bottom: ${spacing.x3};
`;

export const AddClauseContainer = styled.div`
  padding: 0;
`;

export const ConditionalBadge = styled(Badge)`
  padding: ${spacing.quarter} ${spacing.half};
  margin-right: ${spacing.plusHalf};
  font-size: ${fontSizes.s12};
  line-height: ${fontSizes.s12};
  height: ${fontSizes.s24};
`;

export const StyledConditionalBadgeWrapper = styled.div`
  padding: 0 0 ${spacing.plusHalf} 0;
  padding-left: 0;
`;

export const UpdatedStyledConditionalBadgeWrapper = styled.div`
  padding: 0 0 ${spacing.base} 0;
  padding-left: ${spacing.plusHalf};
`;

/** Connecting Lines for And Or Conditions */
export const ConnectingLine = styled.div`
  border-left: 3px solid ${colors.grey5};
  position: absolute;
  left: ${spacing.plusHalf};
  margin-top: ${spacing.half};
  margin-left: ${spacing.eighth};
  top: ${spacing.half};
  height: 100%;
`;

export const StyledOverflowContent = styled.div`
  ${largeShadow}
  height: 100%;
  width: ${pxToRem(236)};
  background: ${colors.white};
  border-color: ${colors.grey5};
  border-radius: ${spacing.quarter};
  padding: ${spacing.threeQuarter};
  margin-left: ${spacing.quarter};
  display: flex;
  flex-direction: column;
  gap: ${spacing.threeQuarter};
`;

export const StyledOverflowTitle = styled.div`
  ${subtitle2};
  text-align: left;
  overflow-wrap: break-word;
`;

export const StyledOverflowCaption = styled.div`
  ${caption}
  color: ${colors.textSecondary};
  display: flex;
`;

export const StyledOverflowCaptionDisabled = styled.div`
  ${caption}
  color: ${colors.textDisabled};
  display: flex;
  align-items: center;
  gap: ${spacing.eighth};
`;

export const StyledDescription = styled.div`
  /* Empty styled component */
`;

export const StyledOverflowInnerWrapper = styled.div`
  background: ${colors.grey1};
  border-radius: ${spacing.threeEights};
  padding: ${spacing.threeQuarter};
`;

export const StyledOverflowInnerBody = styled.div`
  ${body2}
  color: ${colors.textSecondary};
  text-align: left;
`;

export const StyledOverflowInnerCountText = styled.span`
  ${body2}
  color: ${colors.textPrimary};
  font-weight: ${fontWeights.bold};
`;

export const StyledSmallDivider = styled(Divider)`
  margin-top: ${spacing.threeQuarter};
  margin-bottom: ${spacing.threeQuarter};
`;

export const StyledPurpleTextInputSuffix = styled.span`
  color: ${colors.purple90};
`;

export const StyledDisplayConditions = styled.div`
  display: flex;
  flex-direction: column;
`;

export const TemplateEventPopoverContainer = styled.div`
  position: relative;
`;

export const TemplateEventPopoverTrigger = styled(Trigger)`
  position: absolute;
  width: 100%;
`;

export const TemplatePopoverContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half} 0;
  max-width: ${pxToRem(248)};
`;

export const TemplatePopoverContentResult = styled.div`
  ${body2};
  font-weight: ${fontWeights.bold};
`;

export const StyledSecondaryContentText = styled.div`
  ${body2};
  color: ${colors.textSecondary};
`;
